/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

// Component imports
import { Card, Col, Divider, Radio, Row, Select, Space, Switch, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

// Misc imports
import { setSendMessageMode, setSpellCheckLanguage, toggleNotificationSound, togglePushNotifications } from '../../store/liveChat/liveChatThunks';
import { NOTIFICATIONS_PERMISSIONS } from '../../constants/notifications';
import { LIVECHAT_SEND_MESSAGE_MODE } from '../../constants/liveChat';
import { SPELLCHECK_LANGUAGES } from '../../constants/main';
import useTheme from '../../utils/hooks/useTheme';

// Constant declarations
const Notification = window.Notification || window.mozNotification || window.webkitNotification;

const LiveChatAgentSettingsForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { formProps } = useTheme();

  // Store state
  const { pushNotificationsPermission, conversationsSoundEnabled, requestsSoundEnabled, sendMessageMode, spellCheckLanguage } = useSelector((state) => state.livechat);

  // Local state
  const [disablePushSwitch, setDisablePushSwitch] = useState(false);

  useEffect(() => {
    const canTogglePushNotifications = () => {
      setDisablePushSwitch(Notification.permission === NOTIFICATIONS_PERMISSIONS.DENIED);
      if (Notification.permission !== NOTIFICATIONS_PERMISSIONS.GRANTED) {
        dispatch(togglePushNotifications(Notification.permission));
      }
    };

    // Mounting the event listener
    window.addEventListener('focus', canTogglePushNotifications);
    canTogglePushNotifications();
    return () => {
      window.removeEventListener('focus', canTogglePushNotifications);
    };
  }, []);

  const toggleNotificationPermission = (checked) => {
    if (Notification.permission === NOTIFICATIONS_PERMISSIONS.GRANTED) {
      dispatch(togglePushNotifications(checked ? NOTIFICATIONS_PERMISSIONS.GRANTED : NOTIFICATIONS_PERMISSIONS.DENIED));
    } else if (Notification.permission === NOTIFICATIONS_PERMISSIONS.DENIED) {
      dispatch(togglePushNotifications(NOTIFICATIONS_PERMISSIONS.DENIED));
      setDisablePushSwitch(true);
    } else if (Notification.permission === NOTIFICATIONS_PERMISSIONS.DEFAULT) {
      Notification.requestPermission().then(choice => {
        dispatch(togglePushNotifications(choice));
      });
    }
  };

  const onChangeSendMessageMode = (event) => {
    dispatch(setSendMessageMode(event.target.value));
  };

  const onChangeSpellCheckLanguage = (language) => {
    dispatch(setSpellCheckLanguage(language));
  };

  return (
    <Fragment>
      <Card>
        <Row
          wrap
          gutter={formProps.rowGutter}
          className="mb-4"
        >
          <Col xs={formProps.mainColSpan}>
            <Typography.Paragraph className="mb-0">{t('Sound Notifications')}</Typography.Paragraph>
            <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Toggle notification sounds for new requests and messages')}</Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
            <Typography.Paragraph className="mb-2">{t('Conversations Sounds')}</Typography.Paragraph>

            <Switch
              className="mb-4"
              defaultChecked={conversationsSoundEnabled}
              onChange={(checked) => {
                dispatch(toggleNotificationSound(checked));
                if (checked) {
                  document.getElementById('hbfNotificationSound')?.play();
                }
              }}
            />

            <Typography.Paragraph className="mb-2">{t('Requests Sounds')}</Typography.Paragraph>

            <Switch
              defaultChecked={requestsSoundEnabled}
              onChange={(checked) => {
                dispatch(toggleNotificationSound(checked, true));
                if (checked) {
                  document.getElementById('hbfNotificationSound')?.play();
                }
              }}
            />
          </Col>
        </Row>

        <Divider />

        <Row
          wrap
          gutter={formProps.rowGutter}
          className="mb-4"
        >
          <Col xs={formProps.mainColSpan}>
            <Typography.Paragraph className="mb-0">{t('Push Notifications')}</Typography.Paragraph>
            <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Toggle browser notifications sent by Chatbricks Console')}</Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
            <Typography.Paragraph className="mb-2">{t('Browser Notifications')}</Typography.Paragraph>

            <Switch
              className="mb-2"
              disabled={disablePushSwitch}
              checked={pushNotificationsPermission === NOTIFICATIONS_PERMISSIONS.GRANTED}
              onChange={toggleNotificationPermission}
            />

            {
              disablePushSwitch ? (
                <Typography.Paragraph
                  className="mb-0 fs-sm"
                >
                  <WarningOutlined className="mr-1 text-warning"/>
                  {t('You have blocked Chatbricks from sending you notifications.')}
                  <div>{t('You can change this by going to your browser\'s settings.')}</div>
                </Typography.Paragraph>
              ) : null
            }
          </Col>
        </Row>

        <Divider />

        <Row
          wrap
          gutter={formProps.rowGutter}
        >
          <Col xs={formProps.mainColSpan}>
            <Typography.Paragraph className="mb-0">{t('Send Message')}</Typography.Paragraph>
            <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Set your preference about "Send message" behavior.')}</Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
            <Radio.Group value={sendMessageMode} onChange={onChangeSendMessageMode}>
              <Space direction="vertical">
                <Radio
                  value={LIVECHAT_SEND_MESSAGE_MODE.ENTER}
                >
                  {<Trans t={t}>Press <Typography.Text strong italic>Enter</Typography.Text> to send message</Trans>}
                </Radio>
                <Radio
                  value={LIVECHAT_SEND_MESSAGE_MODE.SEND_BUTTON}
                >
                  {<Trans t={t}>Click <Typography.Text strong italic>Send button</Typography.Text> to send message</Trans>}
                </Radio>
              </Space>
            </Radio.Group>
          </Col>
        </Row>

        <Divider />

        <Row
          wrap
          gutter={formProps.rowGutter}
        >
          <Col xs={formProps.mainColSpan}>
            <Typography.Paragraph className="mb-0">{t('Spell Check Settings')}</Typography.Paragraph>
            <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Set your preference about spell check.')}</Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
            <Typography.Paragraph className="mb-2">{t('Spell Check Support')}</Typography.Paragraph>

            <Switch
              className="mb-4"
              defaultChecked={spellCheckLanguage !== null}
              onChange={(checked) => {
                if (checked) {
                  onChangeSpellCheckLanguage(SPELLCHECK_LANGUAGES[0].key);
                } else {
                  onChangeSpellCheckLanguage(null);
                }
              }}
            />

            {
              spellCheckLanguage ? (
                <Fragment>
                  <Typography.Paragraph className="mb-2">{t('Spell Check Language')}</Typography.Paragraph>

                  <Select
                    placeholder={t('Choose a language')}
                    allowClear
                    defaultValue={spellCheckLanguage || SPELLCHECK_LANGUAGES[0].key}
                    onChange={onChangeSpellCheckLanguage}
                    style={{ minWidth: 200 }}
                    showSearch
                    filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
                    options={
                      SPELLCHECK_LANGUAGES
                        .map(language => ({
                          label: <Space size={4}>{language.label}</Space>,
                          value: language.key,
                          searchValue: language.label
                        }))
                    }
                  />
                </Fragment>
              ) : null
            }
          </Col>
        </Row>
      </Card>
    </Fragment>
  );
};

export default LiveChatAgentSettingsForm;
